import React, { useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  DeleteOutlineRounded,
  InfoOutlined,
  SaveOutlined,
  SendRounded,
} from "@mui/icons-material";
import color from "../../../../Constants/colors";
import { DealData, RegistrationStatus } from "../../../../Models/ambassador";
import {
  useDeleteDraft,
  useMyDeal,
  useSaveDraft,
  useSubmitDeal,
} from "../../../../Query/useAmbassador";
import AmbassadorConfirmDialog from "../../Common/AmbassadorConfirmDialog";
import {
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
} from "../../Common/AmbassadorUi";
import { ambassadorPaths } from "../../Common/ambassadorNavigation";
import DealDataSummary from "../DealDataSummary";
import {
  ClassificationStep,
  CustomerStep,
  DataProtectionConfirmation,
  ProjectStep,
} from "./DealFormSteps";
import {
  DealErrors,
  EMPTY_DEAL,
  STEP_KEYS,
  toFormData,
  validateAll,
  validateStep,
} from "./dealFormModel";

interface FormProps {
  dealId: number | null;
  initial: DealData;
  initialStep: number;
}

const SidePanel: React.FC<{ step: number }> = ({ step }) => {
  const { t } = useTranslation("ambassador");
  const stepHint = t(`wizard.sideHints.${STEP_KEYS[step]}`, {
    returnObjects: true,
  }) as {
    title: string;
    text: string;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Panel title={t("wizard.howItWorks.title")}>
        <Box
          component="ol"
          sx={{ m: 0, pl: 2.5, color: color.gray700, fontSize: 14 }}
        >
          {[1, 2, 3, 4].map((index) => (
            <Box component="li" key={index} sx={{ mb: 0.5 }}>
              {t(`wizard.howItWorks.step${index}`)}
            </Box>
          ))}
        </Box>
      </Panel>
      <Panel title={stepHint.title}>
        <Typography variant="body2">{stepHint.text}</Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, color: color.gray500 }}>
          <InfoOutlined sx={{ fontSize: 18, mt: "1px" }} />
          <Typography sx={{ fontSize: 13, color: color.gray500 }}>
            {t("wizard.notConfirmedYet")}
          </Typography>
        </Box>
      </Panel>
    </Box>
  );
};

const DealWizardForm: React.FC<FormProps> = ({
  dealId,
  initial,
  initialStep,
}) => {
  const { t } = useTranslation("ambassador");
  const navigate = useNavigate();
  const [data, setData] = useState<DealData>(initial);
  const [step, setStep] = useState(initialStep);
  const [errors, setErrors] = useState<DealErrors>({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const saveDraft = useSaveDraft();
  const submitDeal = useSubmitDeal();
  const deleteDraft = useDeleteDraft();
  const busy = saveDraft.isPending || submitDeal.isPending;
  const lastStep = STEP_KEYS.length - 1;

  const change = (patch: Partial<DealData>) => {
    setData((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      Object.keys(patch).forEach(
        (field) => delete next[field as keyof DealData],
      );
      return next;
    });
  };

  /** Saves the draft. A new one moves to its own address, so a reload keeps it. */
  const persist = async (notify: boolean, nextStep: number) => {
    const deal = await saveDraft.mutateAsync({ dealId, data, notify });
    if (dealId === null) {
      navigate(ambassadorPaths.editDraft(deal.id), {
        replace: true,
        state: { step: nextStep },
      });
    }
    return deal;
  };

  const save = async () => {
    // Drafts may be incomplete; only values the backend would refuse block it.
    const formatErrors = validateStep(
      { ...EMPTY_DEAL, contact_email: data.contact_email },
      0,
    );
    if (formatErrors.contact_email) {
      setErrors({ contact_email: formatErrors.contact_email });
      setStep(0);
      return;
    }
    await persist(true, step).catch(() => undefined);
  };

  const next = async () => {
    const stepErrors = validateStep(data, step);
    if (Object.keys(stepErrors).length) {
      setErrors(stepErrors);
      return;
    }
    try {
      await persist(false, step + 1);
      setStep(step + 1);
    } catch {
      // The request already showed what went wrong.
    }
  };

  const submit = async () => {
    const invalid = validateAll(data);
    if (invalid) {
      setErrors(invalid.errors);
      setStep(invalid.step);
      return;
    }
    try {
      const id =
        dealId ??
        (await saveDraft.mutateAsync({ dealId, data, notify: false })).id;
      const deal = await submitDeal.mutateAsync({ dealId: id, data });
      navigate(ambassadorPaths.myDeal(deal.id), { state: { submitted: true } });
    } catch {
      // The request already showed what went wrong.
    }
  };

  // A step can be opened once every step before it is complete.
  const canOpen = (index: number) =>
    Array.from({ length: index }, (_, previous) => previous).every(
      (previous) => !Object.keys(validateStep(data, previous)).length,
    );

  const stepProps = { data, errors, onChange: change };
  const renderStep = () => {
    if (step === 0) return <CustomerStep {...stepProps} />;
    if (step === 1) return <ProjectStep {...stepProps} />;
    if (step === 2) return <ClassificationStep {...stepProps} />;
    return (
      <>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("wizard.reviewIntro")}
        </Typography>
        <DealDataSummary data={data} onEdit={setStep} />
        <Box sx={{ mt: 3 }}>
          <DataProtectionConfirmation {...stepProps} />
        </Box>
      </>
    );
  };

  return (
    <>
      <PageHeader
        title={t("wizard.title")}
        subtitle={t("wizard.subtitle")}
        actions={
          dealId !== null && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineRounded />}
              onClick={() => setDeleteOpen(true)}
            >
              {t("wizard.deleteDraft")}
            </Button>
          )
        }
      />

      <Box sx={{ mb: 3 }}>
        <Panel sx={{ py: 2 }}>
          <Stepper nonLinear activeStep={step} alternativeLabel>
            {STEP_KEYS.map((key, index) => (
              <Step key={key} completed={index < step && canOpen(index + 1)}>
                <StepButton
                  onClick={() => setStep(index)}
                  disabled={index === step || !canOpen(index)}
                >
                  <StepLabel>{t(`wizard.steps.${key}`)}</StepLabel>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Panel>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) 320px",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Panel
          title={t("wizard.stepTitle", {
            number: step + 1,
            title: t(`wizard.steps.${STEP_KEYS[step]}`),
          })}
        >
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("wizard.errors.summary")}
            </Alert>
          )}
          {renderStep()}

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${color.gray100}`,
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: 12, color: color.gray500 }}>
              {t(step === 1 ? "wizard.nonBindingHint" : "wizard.requiredHint")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {step > 0 && (
                <Button
                  color="secondary"
                  startIcon={<ArrowBackRounded />}
                  onClick={() => setStep(step - 1)}
                  disabled={busy}
                >
                  {t("common.back")}
                </Button>
              )}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SaveOutlined />}
                onClick={save}
                disabled={busy}
              >
                {t("wizard.saveDraft")}
              </Button>
              {step < lastStep ? (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardRounded />}
                  onClick={next}
                  disabled={busy}
                >
                  {t("wizard.nextTo", {
                    step: t(`wizard.steps.${STEP_KEYS[step + 1]}`),
                  })}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  endIcon={<SendRounded />}
                  onClick={submit}
                  disabled={busy}
                >
                  {t("wizard.submit")}
                </Button>
              )}
            </Box>
          </Box>
        </Panel>

        <SidePanel step={step} />
      </Box>

      <AmbassadorConfirmDialog
        open={deleteOpen}
        title={t("wizard.deleteTitle")}
        text={t("wizard.deleteText")}
        confirmLabel={t("common.delete")}
        danger
        pending={deleteDraft.isPending}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() =>
          dealId !== null &&
          deleteDraft.mutate(dealId, {
            onSuccess: () =>
              navigate(ambassadorPaths.myDeals, { replace: true }),
          })
        }
      />
    </>
  );
};

/** "New Deal Rec": the four-step form for a new deal or a saved draft. */
const DealWizard: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const dealId = params.dealId ? Number(params.dealId) : null;
  const deal = useMyDeal(dealId ?? undefined);
  const initialStep = (location.state as { step?: number } | null)?.step ?? 0;

  if (dealId === null) {
    return (
      <DealWizardForm
        key="new"
        dealId={null}
        initial={EMPTY_DEAL}
        initialStep={0}
      />
    );
  }
  if (deal.isLoading) return <LoadingState />;
  if (deal.isError || !deal.data)
    return <ErrorState onRetry={() => deal.refetch()} />;
  if (deal.data.registration_status !== RegistrationStatus.DRAFT) {
    return <Navigate to={ambassadorPaths.myDeal(dealId)} replace />;
  }
  return (
    <DealWizardForm
      key={dealId}
      dealId={dealId}
      initial={toFormData(deal.data)}
      initialStep={initialStep}
    />
  );
};

export default DealWizard;
